import React from 'react';
import { Tooltip } from 'antd';
import styled from 'styled-components';

import { ow, owType, px } from '../theme/tokens';
import { ACTION_COPY } from './overwatchActions';
import { IOverwatchActionRequest } from './useOverwatchAction';

/** A drawn but disabled action control, for while discovery has not confirmed the action. */
export const NotAvailableYet: React.FC<{ children: React.ReactElement }> = ({ children }) => (
  <Tooltip title={ACTION_COPY.notAvailable} trigger={['hover', 'focus']}>
    <span>{children}</span>
  </Tooltip>
);

const Control = styled.span`
  position: relative;
  display: inline-flex;
`;

// Out of the flow, so a failure does not reflow the header or card the button sits in.
const InlineError = styled.span`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1;
  margin-top: ${px(4)};
  font-size: ${owType.caption.size};
  line-height: ${owType.caption.line};
  color: ${ow.danger};
  white-space: nowrap;
`;

const InlineNote = styled(InlineError)`
  color: ${ow.textSecondary};
`;

interface IActionRequestButtonProps {
  request: IOverwatchActionRequest;
  /** The section's own styled button. */
  button: React.ElementType;
  children: React.ReactNode;
}

const ActionRequestButton: React.FC<IActionRequestButtonProps> = ({
  request,
  button: Button,
  children
}) => {
  if (request.availability === 'hidden') return null;

  if (request.availability === 'disabled') {
    return (
      <NotAvailableYet>
        <Button type="button" disabled>
          {children}
        </Button>
      </NotAvailableYet>
    );
  }

  const { status } = request;

  return (
    <Control>
      <Button
        type="button"
        disabled={status !== 'idle'}
        aria-busy={status === 'sending' || undefined}
        onClick={request.send}
      >
        {status === 'sending' ? <span>{ACTION_COPY.sending}</span> : null}
        {status === 'sent' ? <span>{ACTION_COPY.requestSent}</span> : null}
        {status === 'idle' || status === 'failed' ? children : null}
      </Button>
      <InlineError role="alert">{request.error}</InlineError>
      {status === 'sent' && request.sentOn ? (
        <InlineNote role="status" data-testid="ow-action-sent-on">
          {ACTION_COPY.sentOn(request.sentOn)}
        </InlineNote>
      ) : null}
    </Control>
  );
};

export default ActionRequestButton;
